import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Check, Loader2, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HostStatement } from "./types";
import { formatCentsAdmin, formatDate } from "./helpers";

export function ReferHostStatementsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [quarter, setQuarter] = useState(() => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${q}`;
  });

  const query = useQuery<HostStatement[]>({
    queryKey: ["/api/admin/refer-host/statements"],
    queryFn: async () => {
      const res = await fetch("/api/admin/refer-host/statements", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await fetch(`/api/admin/refer-host/statements/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Failed to update");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/refer-host/statements"] });
      toast({ title: "Statement updated" });
    },
    onError: (err: unknown) => toast({ title: err instanceof Error ? err.message : "Failed to update statement", variant: "destructive" }),
  });

  // "Approve & Send" — fires the dedicated server endpoint that emails the
  // PDF to the referrer AND initiates the Mercury Bank ACH payout (currently
  // stubbed). Both side effects must succeed for the statement to flip from
  // `draft` to `sent`; on failure the statement stays in `draft` so the admin
  // can retry once the failure is resolved.
  const approveMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/refer-host/statements/${id}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Approve failed");
      return body;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/refer-host/statements"] });
      const ref = data?.payment?.reference;
      toast({
        title: "Statement approved & sent",
        description: ref ? `Payout reference: ${ref} (Mercury ACH stub)` : undefined,
      });
    },
    onError: (err: unknown) =>
      toast({
        title: err instanceof Error ? err.message : "Approve failed",
        variant: "destructive",
      }),
  });

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await fetch("/api/admin/refer-host/statements/generate", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quarter }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");
      toast({ title: `Generated ${data.generated ?? data.count ?? 0} statement(s) for ${quarter}` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/refer-host/statements"] });
    } catch (err: unknown) {
      toast({ title: err instanceof Error ? err.message : "Failed to generate", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  }

  const statements = query.data || [];
  const statusColors: Record<string, string> = {
    draft: "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200",
    sent: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
    paid: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300",
    overdue: "bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200",
    voided: "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300",
  };

  const draftCount = statements.filter(s => s.status === "draft").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-500" />
          Quarterly Payouts
          {draftCount > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 font-semibold"
              data-testid="badge-pending-review-count"
            >
              {draftCount} pending review
            </span>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Review each draft statement, then click <strong>Approve &amp; Send</strong> to email the PDF
          to the referrer and trigger the Mercury Bank ACH payout. The Mercury integration is
          currently stubbed — payouts are logged but not yet sent to a real bank.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Quarter:</label>
            <Input
              value={quarter}
              onChange={e => setQuarter(e.target.value)}
              placeholder="2024-Q4"
              className="w-32"
              data-testid="input-generate-quarter"
            />
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="gap-2"
            data-testid="button-generate-statements"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
            Generate Statements
          </Button>
          <p className="text-xs text-muted-foreground">Generates one draft statement per referrer who has pending accruals in the given quarter.</p>
        </div>

        {query.isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : statements.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No statements yet.</p>
        ) : (
          <Table data-testid="table-refer-host-statements">
            <TableHeader>
              <TableRow>
                <TableHead>Quarter</TableHead>
                <TableHead>Referrer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead>Payout Ref</TableHead>
                <TableHead>Paid At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statements.map((s) => (
                <TableRow key={s.id} data-testid={`row-statement-${s.id}`}>
                  <TableCell>{s.periodQuarter}</TableCell>
                  <TableCell className="font-medium">
                    <div data-testid={`text-referrer-name-${s.id}`}>{s.referrerName}</div>
                    <div className="text-xs text-muted-foreground" data-testid={`text-referrer-email-${s.id}`}>{s.referrerEmail}</div>
                  </TableCell>
                  <TableCell className="text-xs">{s.referrerType === "user" ? "App User" : "Partner"}</TableCell>
                  <TableCell className="font-medium text-green-700" data-testid={`text-total-${s.id}`}>{formatCentsAdmin(s.totalCents)}</TableCell>
                  <TableCell>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[s.status] || "bg-gray-100 text-gray-600"}`}
                      data-testid={`status-statement-${s.id}`}
                    >
                      {s.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.pdfUrl ? (
                      <a
                        href={s.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                        data-testid={`link-pdf-${s.id}`}
                      >
                        Preview
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground" data-testid={`text-approved-${s.id}`}>
                    {s.approvedAt ? formatDate(s.approvedAt) : "—"}
                  </TableCell>
                  <TableCell className="text-xs font-mono" data-testid={`text-payment-ref-${s.id}`}>
                    {s.paymentReference ? (
                      <span title={s.paymentMethod ?? undefined}>{s.paymentReference}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {s.paidAt ? formatDate(s.paidAt) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 flex-wrap">
                      {s.status === "draft" && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 gap-1"
                          onClick={() => approveMutation.mutate(s.id)}
                          disabled={approveMutation.isPending}
                          data-testid={`button-approve-${s.id}`}
                        >
                          <Check className="h-3 w-3" />
                          Approve & Send
                        </Button>
                      )}
                      {s.status === "sent" && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 gap-1"
                          onClick={() => updateMutation.mutate({ id: s.id, status: "paid" })}
                          disabled={updateMutation.isPending}
                          data-testid={`button-mark-paid-${s.id}`}
                        >
                          <Check className="h-3 w-3" />
                          Mark Paid
                        </Button>
                      )}
                      {s.status === "sent" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateMutation.mutate({ id: s.id, status: "overdue" })}
                          disabled={updateMutation.isPending}
                          data-testid={`button-mark-overdue-${s.id}`}
                        >
                          Mark Overdue
                        </Button>
                      )}
                      {s.status !== "voided" && s.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateMutation.mutate({ id: s.id, status: "voided" })}
                          disabled={updateMutation.isPending}
                          data-testid={`button-void-${s.id}`}
                        >
                          Void
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
